import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Images } from 'lucide-react';
import type { MiniSiteGallery } from '@worki/shared';
import { Modal } from './Modal.js';

type Album = Pick<MiniSiteGallery, 'id' | 'name' | 'description' | 'images'>;

export function QuoteAlbums({ galleries, businessName = '' }: { galleries: Album[]; businessName?: string }) {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<{ id: string; index: number } | null>(null);
  const selected = galleries.find((gallery) => gallery.id === selection?.id);
  if (!galleries.length) return null;

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold">
        {businessName ? t('quotes.additionalProjects', { name: businessName }) : t('quotes.attachedAlbums')}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {galleries.map((gallery) => (
          <button
            key={gallery.id}
            type="button"
            aria-haspopup="dialog"
            onClick={() => setSelection({ id: gallery.id, index: 0 })}
            className="overflow-hidden rounded-xl border border-border text-start hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {gallery.images[0] ? (
              <img src={gallery.images[0].url} alt={gallery.images[0].altText ?? gallery.name} className="w-full aspect-[4/3] object-cover" loading="lazy" />
            ) : (
              <div className="aspect-[4/3] flex items-center justify-center bg-muted text-muted-foreground"><Images className="h-8 w-8" aria-hidden="true" /></div>
            )}
            <span className="block p-3 text-sm font-medium">{gallery.name}</span>
          </button>
        ))}
      </div>
      {selected && selection && (
        <Modal open title={selected.name} onClose={() => setSelection(null)} size="full">
          <QuoteAlbumImages
            gallery={selected}
            index={Math.min(selection.index, Math.max(0, selected.images.length - 1))}
            onMove={(direction) => setSelection((current) => current && ({
              ...current,
              index: Math.max(0, Math.min(selected.images.length - 1, current.index + direction)),
            }))}
          />
        </Modal>
      )}
    </section>
  );
}

/** The selected album's content; the shared modal supplies focus, Escape and close. */
export function QuoteAlbumImages({ gallery, index, onMove }: { gallery: Album; index: number; onMove: (direction: number) => void }) {
  const { t } = useTranslation();
  const image = gallery.images[index];
  return (
    <div className="space-y-3">
      {image && <img src={image.url} alt={image.altText ?? gallery.name} className="w-full max-h-[60vh] object-contain rounded-lg" />}
      {gallery.images.length > 1 && (
        <div className="flex items-center justify-between gap-3">
          <button type="button" aria-label={t('common.previous')} disabled={index === 0} onClick={() => onMove(-1)} className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center disabled:opacity-40">
            <ChevronLeft className="h-5 w-5 rtl:rotate-180" aria-hidden="true" />
          </button>
          <span aria-live="polite" className="text-sm text-muted-foreground ltr-nums">{index + 1} / {gallery.images.length}</span>
          <button type="button" aria-label={t('common.next')} disabled={index === gallery.images.length - 1} onClick={() => onMove(1)} className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center disabled:opacity-40">
            <ChevronRight className="h-5 w-5 rtl:rotate-180" aria-hidden="true" />
          </button>
        </div>
      )}
      {gallery.description && <p className="text-sm whitespace-pre-wrap text-muted-foreground">{gallery.description}</p>}
    </div>
  );
}
